// One Intl.DateTimeFormat per time zone, per set of options. ISSUE-1062, moved from
// services/events-api/src/lib/zonedFormatter.ts to @sportsmet/errors by ISSUE-1136.
//
// On Bun 1.3.11, the runtime every service image runs, a new
// Intl.DateTimeFormat per call keeps its memory after GC. Measured on
// 2026-10-10 with formatKickoff's own options and four zones: RSS went 37,
// 103, 174, 316 and 599 MiB at 1,000, 10,000, 20,000, 40,000 and 80,000
// calls, about 7 MiB per 1,000, and none of it came back after an idle GC.
// One reused formatter per zone stayed at 81 MiB over 320,000 calls.
// f265a832f fixed formatDay, the per-row case on the internal reads that
// contest-api-jobs calls all day. This covers the builds that were left: the
// kickoff label for every scheduled row on the fan reads, every head to head
// date, and the time zone check on every request.
//
// A formatter is immutable, so sharing one changes no output. A zone can
// arrive in a request body, and Intl reads a zone name in any letter case, so
// the cache is keyed by the lowercased name (case variants of one zone share
// one formatter instead of each taking a slot) and capped. Past the cap a
// call still gets a correct formatter, just not a cached one.
//
// Only an ASCII name is cached. Every IANA zone name is ASCII, but
// toLowerCase folds some other characters onto ASCII letters: the Kelvin sign
// U+212A becomes "k", so "Asia/\u212Aolkata" would hit the Asia/Kolkata entry
// although Intl rejects that spelling. A name with any other character goes
// straight to the constructor, which accepts or rejects it as it always did.
export const ZONED_FORMATTER_CACHE_MAX_ENTRIES = 512;

const ASCII_ONLY = /^[\x20-\x7e]*$/;

/**
 * A function returning the shared formatter for `timeZone`, built with
 * `options`. Throws the constructor's RangeError for an unknown zone, as
 * `new Intl.DateTimeFormat` does, and never caches a failure.
 */
export function createZonedFormatterCache(
  options: Omit<Intl.DateTimeFormatOptions, "timeZone">,
  maxEntries: number = ZONED_FORMATTER_CACHE_MAX_ENTRIES,
): (timeZone: string) => Intl.DateTimeFormat {
  const formatterByTimeZone = new Map<string, Intl.DateTimeFormat>();
  return (timeZone) => {
    if (!ASCII_ONLY.test(timeZone)) {
      return new Intl.DateTimeFormat("en-US", { ...options, timeZone });
    }
    const cacheKey = timeZone.toLowerCase();
    const cached = formatterByTimeZone.get(cacheKey);
    if (cached !== undefined) return cached;
    const created = new Intl.DateTimeFormat("en-US", { ...options, timeZone });
    if (formatterByTimeZone.size < maxEntries) {
      formatterByTimeZone.set(cacheKey, created);
    }
    return created;
  };
}
